import React, { useState, useEffect } from 'react';
import { Calendar, Clock, ChevronLeft, ChevronRight, Check } from 'lucide-react';

const CalendarSlotPicker = ({ doctorId, onSlotSelect, doctorService, showTimeSlots = true, consultationType = 'priority' }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotsData, setSlotsData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to format date in local timezone (avoids UTC conversion issues)
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load slots for the current month
  useEffect(() => {
    if (!doctorId) return;

    const loadMonthSlots = async () => {
      setLoading(true);
      setError(null);

      try {
        // Get first and last day of current month
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        // Load slots for each day in the visible month
        const days = [];
        for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
          days.push(new Date(d));
        }

        // Load slots for all days (in parallel)
        const slotPromises = days.map(async (day) => {
          const dateStr = formatDateLocal(day);
          try {
            const response = await doctorService.getDoctorSlots(doctorId, dateStr);
            return {
              date: dateStr,
              data: response
            };
          } catch (err) {
            console.error(`Failed to load slots for ${dateStr}:`, err);
            return { date: dateStr, data: null };
          }
        });

        const results = await Promise.all(slotPromises);

        // Build slots map
        const slotsMap = {};
        results.forEach(({ date, data }) => {
          if (data && data.slots) {
            const availableSlots = data.slots.filter(slot => slot.available);
            slotsMap[date] = availableSlots;
            console.log(`📅 Appointment Date: ${date} - Total slots: ${data.slots.length}, Available: ${availableSlots.length}`, {
              firstSlot: data.slots[0]?.time,
              firstAvailable: availableSlots[0]?.time,
              lastAvailable: availableSlots[availableSlots.length - 1]?.time
            });
          }
        });

        console.log('📊 Appointment slots loaded:', slotsMap);
        setSlotsData(slotsMap);
      } catch (err) {
        setError('Failed to load slots. Please try again.');
        console.error('Error loading month slots:', err);
      } finally {
        setLoading(false);
      }
    };

    loadMonthSlots();
  }, [doctorId, currentDate, doctorService]);

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    // Only consider dates BEFORE today as past (today is NOT past)
    return compareDate.getTime() < today.getTime();
  };

  const hasSlots = (date) => {
    const dateStr = formatDateLocal(date);
    return slotsData[dateStr] && slotsData[dateStr].length > 0;
  };

  const getAvailableSlotsCount = (date) => {
    const dateStr = formatDateLocal(date);
    return slotsData[dateStr]?.length || 0;
  };

  const formatTime = (timeStr) => {
    // Handle both "HH:MM AM/PM" and "HH:MM:SS" formats
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }

    const time = new Date(`2000-01-01T${timeStr}`);
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const handleDateSelect = (date) => {
    // Allow selecting today and future dates that have available slots
    if (isPast(date)) {
      console.log('Cannot select past date:', date);
      return;
    }
    if (!hasSlots(date)) {
      console.log('No slots available for:', date);
      return;
    }
    setSelectedDate(date);
    setSelectedSlot(null);
    
    // For normal consultation, immediately notify parent with just the date
    if (!showTimeSlots && onSlotSelect) {
      onSlotSelect({
        date: formatDateLocal(date),
        time: null,
        datetime: null
      });
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    if (onSlotSelect && selectedDate) {
      onSlotSelect({
        date: formatDateLocal(selectedDate),
        time: slot.time,
        datetime: slot.datetime
      });
    }
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
    const days = [];
    const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    // Empty cells before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <div key={`empty-${i}`} className="aspect-square p-2"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const isSelected = selectedDate &&
        date.getDate() === selectedDate.getDate() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getFullYear() === selectedDate.getFullYear();
      const past = isPast(date);
      const today = isToday(date);
      const slots = hasSlots(date);
      const slotsCount = getAvailableSlotsCount(date);

      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(date)}
          disabled={past || !slots}
          title={
            past
              ? 'Date has passed'
              : !slots
              ? today
                ? 'No slots available for today - please contact support'
                : 'No slots available'
              : today
              ? `Today - ${slotsCount} slot${slotsCount !== 1 ? 's' : ''} available`
              : `${slotsCount} slot${slotsCount !== 1 ? 's' : ''} available`
          }
          className={`aspect-square p-1.5 rounded-lg border-2 transition-all relative overflow-hidden ${
            isSelected
              ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-lg'
              : past
              ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
              : !slots
              ? today
                ? 'border-orange-300 bg-orange-50 text-orange-600 cursor-not-allowed'
                : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
              : today
              ? 'border-blue-500 bg-blue-100 hover:border-blue-600 hover:bg-blue-200 text-blue-900 font-bold'
              : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50 text-gray-900'
          } ${today && !isSelected && slots ? 'ring-2 ring-blue-400' : ''}`}
        >
          <div className="flex flex-col items-center justify-center h-full w-full relative">
            <span className={`text-sm font-semibold leading-none ${today ? 'text-blue-700' : ''}`}>
              {day}
            </span>
            {slots && !past && (
              <span className={`text-[10px] mt-0.5 font-medium leading-tight ${today ? 'text-blue-600' : 'text-green-600'}`}>
                {slotsCount}
              </span>
            )}
            {today && slots && (
              <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"></span>
            )}
            {today && !slots && (
              <span className="absolute top-0 right-0.5 text-[10px]">⚠️</span>
            )}
          </div>
        </button>
      );
    }

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handlePrevMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors bg-transparent border-0 w-auto"
            style={{ background: 'transparent', width: 'auto', border: 'none' }}
            aria-label="Previous month"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold text-gray-900">{monthYear}</h3>
          </div>
          <button
            onClick={handleNextMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors bg-transparent border-0 w-auto"
            style={{ background: 'transparent', width: 'auto', border: 'none' }}
            aria-label="Next month"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-2">
          {days}
        </div>

        {/* Legend */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-600 mb-3">Available 7 days a week (Mon-Sun) • 11:00 AM - 4:00 PM</p>
          <div className="flex flex-wrap gap-4 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-blue-600 bg-blue-50"></div>
              <span>Selected</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-gray-300 bg-white"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-gray-200 bg-gray-100"></div>
              <span>No slots</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTimeSlots = () => {
    if (!selectedDate) {
      return (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Date</h3>
          <p className="text-gray-600">
            Choose a date from the calendar to view available time slots
          </p>
        </div>
      );
    }

    const dateStr = formatDateLocal(selectedDate);
    const slots = slotsData[dateStr] || [];

    if (slots.length === 0) {
      return (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Available Slots</h3>
          <p className="text-gray-600">
            No time slots available for {selectedDate.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-bold text-gray-900">
            {selectedDate.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
          </h3>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          {slots.length} available slot{slots.length !== 1 ? 's' : ''} - Select your preferred time
        </p>

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 max-h-96 overflow-y-auto">
          {slots.map((slot, index) => {
            const isSelected = selectedSlot && selectedSlot.time === slot.time;
            return (
              <button
                key={index}
                onClick={() => handleSlotSelect(slot)}
                className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                  isSelected
                    ? 'border-blue-600 bg-blue-600 text-white shadow-lg'
                    : 'border-gray-300 bg-white text-gray-900 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <div className="flex items-center justify-center gap-1">
                  {isSelected && <Check className="w-4 h-4" />}
                  <span className="text-sm">{formatTime(slot.time)}</span>
                </div>
              </button>
            );
          })}
        </div>

        {selectedSlot && (
          <div className="mt-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-800">
              <Check className="w-5 h-5" />
              <div>
                <p className="font-semibold">Slot Selected</p>
                <p className="text-sm">
                  {selectedDate.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })} at {formatTime(selectedSlot.time)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading && Object.keys(slotsData).length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12 text-center">
        <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-600">Loading available slots...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-semibold mb-2">Error Loading Slots</p>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showTimeSlots ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendar */}
          <div>{renderCalendar()}</div>

          {/* Time Slots - Only for Priority */}
          <div>{renderTimeSlots()}</div>
        </div>
      ) : (
        <div>
          {/* Calendar Only - For Normal Consultation */}
          {renderCalendar()}
          {selectedDate && (
            <div className="mt-6 p-6 bg-blue-50 border-2 border-blue-200 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="bg-blue-600 text-white rounded-full p-2">
                  <Check className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-blue-900 text-lg">Date Selected</p>
                  <p className="text-blue-700">
                    {selectedDate.toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                  <p className="text-sm text-blue-600 mt-2">
                    <strong>Normal Consultation:</strong> Doctor will see you based on arrival time at the hospital
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarSlotPicker;
